import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { TextField } from '@/components/ui/Field'

/**
 * Подтверждение деструктивного действия (docs/10 §4, §9): требует ввода имени
 * ресурса и отделено от основных действий. Кнопка «Удалить» до совпадения имени
 * задизейблена — случайный Enter ничего не удалит.
 */
export interface ConfirmDestructiveProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  body: string
  /** Что должен ввести пользователь — обычно имя бота. */
  expected: string
  confirmLabel?: string
  loading?: boolean
}

export function ConfirmDestructive({
  open,
  onClose,
  onConfirm,
  title,
  body,
  expected,
  confirmLabel,
  loading = false
}: ConfirmDestructiveProps): JSX.Element {
  const { t } = useTranslation()
  const [value, setValue] = useState('')

  useEffect(() => {
    if (open) setValue('')
  }, [open])

  const matches = value.trim() === expected

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={body}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={loading}
            onClick={() => {
              if (matches) onConfirm()
            }}
          >
            {confirmLabel ?? t('confirm.confirmDelete')}
          </Button>
        </>
      }
    >
      <TextField
        label={t('confirm.typeName')}
        placeholder={t('confirm.namePlaceholder')}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={event => setValue(event.target.value)}
        error={value !== '' && !matches ? t('confirm.mismatch') : null}
        className="font-mono"
      />
    </Dialog>
  )
}
